// Fatia S5b: consumo de IA (por dia/tarefa/provedor/modelo, top contas) e editor de rotas por tarefa.
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { num } from '@impulso/shared'
import { AI_PROVIDERS, type AiProviderName, type AiRouteDto } from '@impulso/shared/api/admin'
import { errorMessage } from '../../lib/api'
import { Empty, ErrorState, Loading, PageHead, Switch, Tabs } from '../../ui'
import { useToast } from '../../ui/toast'
import { AdminNav } from '../../features/admin/AdminNav'
import { useAiRoutes, useAiUsage, useSaveAiRoutes, usd } from '../../features/admin/api'

export default function AdminAi() {
  const [days, setDays] = useState<'1' | '7' | '30'>('7')
  const u = useAiUsage(Number(days))
  return (
    <>
      <PageHead title="IA" sub="Consumo por tarefa e conta, e qual modelo atende cada tarefa." />
      <AdminNav />
      <div className="stack">
        <Tabs value={days} onChange={setDays} options={[{ value: '1', label: 'Últimas 24 h' }, { value: '7', label: '7 dias' }, { value: '30', label: '30 dias' }]} />
        {u.isLoading && <div className="card"><Loading lines={4} /></div>}
        {u.error && <ErrorState error={u.error} onRetry={() => u.refetch()} />}
        {u.data && (
          <>
            <div className="grid g-4">
              <div className="card kpi"><div className="label">Custo</div><div className="value">{usd(u.data.totals.costUsd)}</div><div className="hint">no período</div></div>
              <div className="card kpi"><div className="label">Chamadas</div><div className="value">{num(u.data.totals.requests)}</div><div className="hint">{num(u.data.totals.errors)} com erro</div></div>
              <div className="card kpi"><div className="label">Tokens de entrada</div><div className="value">{num(u.data.totals.inputTokens)}</div><div className="hint">&nbsp;</div></div>
              <div className="card kpi"><div className="label">Tokens de saída</div><div className="value">{num(u.data.totals.outputTokens)}</div><div className="hint">&nbsp;</div></div>
            </div>
            <div className="grid g-main">
              <div className="card flush">
                <h3 style={{ padding: '16px 16px 8px' }}>Por dia, tarefa e modelo</h3>
                {u.data.rows.length === 0 ? <p className="small muted" style={{ padding: 16 }}>Nenhuma chamada no período.</p> : (
                  <div className="table-wrap"><table className="table stack-sm">
                    <thead><tr><th>Dia</th><th>Tarefa</th><th>Modelo</th><th className="right">Chamadas</th><th className="right">Erros</th><th className="right">Custo</th></tr></thead>
                    <tbody>{u.data.rows.map((r) => (
                      <tr key={`${r.day}-${r.task}-${r.provider}-${r.model}`}>
                        <td className="small nowrap" data-label="Dia">{r.day.split('-').reverse().join('/')}</td>
                        <td className="small" data-label="Tarefa">{r.task}</td>
                        <td className="small" data-label="Modelo">{r.provider} · {r.model}</td>
                        <td className="right" data-label="Chamadas">{num(r.requests)}</td>
                        <td className="right" data-label="Erros">{r.errors > 0 ? <span className="badge red">{r.errors}</span> : 0}</td>
                        <td className="right" data-label="Custo">{usd(r.costUsd)}</td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                )}
              </div>
              <div className="card flush">
                <h3 style={{ padding: '16px 16px 8px' }}>Contas que mais gastam</h3>
                {u.data.topAccounts.length === 0 ? <p className="small muted" style={{ padding: 16 }}>Nada ainda.</p> : (
                  <div className="list">{u.data.topAccounts.map((a) => (
                    <div key={a.accountId ?? 'sistema'} className="list-item between" style={{ padding: '10px 16px' }}>
                      <span className="small">{a.accountId ? <Link to={`/admin/contas/${a.accountId}`} style={{ color: 'var(--primary)' }}>{a.accountName ?? a.accountId}</Link> : 'Sistema'}</span>
                      <span className="small"><strong>{usd(a.costUsd)}</strong> <span className="muted">· {num(a.requests)}</span></span>
                    </div>
                  ))}</div>
                )}
              </div>
            </div>
          </>
        )}
        <RoutesEditor />
      </div>
    </>
  )
}

const EMPTY: AiRouteDto = { task: '', provider: 'anthropic', model: '', effort: null, maxOutputTokens: 1024, fallbackProvider: null, fallbackModel: null, enabled: true }

function RoutesEditor() {
  const q = useAiRoutes()
  const save = useSaveAiRoutes()
  const toast = useToast()
  const [edit, setEdit] = useState<AiRouteDto | null>(null)
  const [isNew, setIsNew] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const submit = () => {
    if (!edit) return
    setErr(null)
    save.mutate([edit], { onSuccess: () => { toast('Rota salva.'); setEdit(null) }, onError: (e) => setErr(errorMessage(e)) })
  }
  const upd = (p: Partial<AiRouteDto>) => setEdit((e) => (e ? { ...e, ...p } : e))

  return (
    <div className="card flush">
      <div className="row between" style={{ padding: '16px 16px 8px' }}>
        <h3>Rotas por tarefa</h3>
        <button className="btn sm" onClick={() => { setEdit({ ...EMPTY }); setIsNew(true); setErr(null) }}>Nova rota</button>
      </div>
      {q.isLoading && <div style={{ padding: 16 }}><Loading /></div>}
      {q.error && <ErrorState error={q.error} onRetry={() => q.refetch()} />}
      {q.data && q.data.items.length === 0 && !edit && <div style={{ padding: 16 }}><Empty title="Nenhuma rota cadastrada">O módulo de IA usa os padrões dele até você cadastrar uma rota.</Empty></div>}
      {q.data && q.data.items.length > 0 && (
        <div className="table-wrap"><table className="table stack-sm">
          <thead><tr><th>Tarefa</th><th>Modelo</th><th>Reserva</th><th className="right">Máx. saída</th><th>Ativa</th><th /></tr></thead>
          <tbody>{q.data.items.map((r) => (
            <tr key={r.task}>
              <td className="small strong">{r.task}</td>
              <td className="small" data-label="Modelo">{r.provider} · {r.model}{r.effort ? ` (${r.effort})` : ''}</td>
              <td className="small muted" data-label="Reserva">{r.fallbackProvider ? `${r.fallbackProvider} · ${r.fallbackModel}` : '—'}</td>
              <td className="right small" data-label="Máx. saída">{num(r.maxOutputTokens)}</td>
              <td data-label="Ativa">{r.enabled ? <span className="badge green">Sim</span> : <span className="badge">Não</span>}</td>
              <td data-label=""><button className="btn sm" onClick={() => { setEdit({ ...r }); setIsNew(false); setErr(null) }}>Editar</button></td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      {edit && (
        <div className="stack" style={{ padding: 16, borderTop: '1px solid var(--border)', gap: 12 }}>
          <h3>{isNew ? 'Nova rota' : `Editar ${edit.task}`}</h3>
          <div className="grid g-3" style={{ gap: 12 }}>
            <div className="field"><label htmlFor="rt">Tarefa</label><input id="rt" className="input" value={edit.task} disabled={!isNew} onChange={(e) => upd({ task: e.target.value })} /></div>
            <div className="field"><label htmlFor="rp">Provedor</label>
              <select id="rp" className="select" value={edit.provider} onChange={(e) => upd({ provider: e.target.value as AiProviderName })}>{AI_PROVIDERS.map((p) => <option key={p}>{p}</option>)}</select>
            </div>
            <div className="field"><label htmlFor="rm">Modelo</label><input id="rm" className="input" value={edit.model} onChange={(e) => upd({ model: e.target.value })} /></div>
            <div className="field"><label htmlFor="re">Esforço (opcional)</label><input id="re" className="input" value={edit.effort ?? ''} onChange={(e) => upd({ effort: e.target.value || null })} /></div>
            <div className="field"><label htmlFor="rx">Máx. tokens de saída</label><input id="rx" className="input" type="number" min={1} value={edit.maxOutputTokens} onChange={(e) => upd({ maxOutputTokens: Number(e.target.value) })} /></div>
            <div className="field"><label>Ativa</label><Switch label="Rota ativa" on={edit.enabled} onChange={(v) => upd({ enabled: v })} /></div>
            <div className="field"><label htmlFor="rfp">Provedor de reserva</label>
              <select id="rfp" className="select" value={edit.fallbackProvider ?? ''} onChange={(e) => upd({ fallbackProvider: (e.target.value || null) as AiProviderName | null })}>
                <option value="">Nenhum</option>{AI_PROVIDERS.map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div className="field"><label htmlFor="rfm">Modelo de reserva</label><input id="rfm" className="input" value={edit.fallbackModel ?? ''} onChange={(e) => upd({ fallbackModel: e.target.value || null })} /></div>
          </div>
          {err && <div className="form-error" role="alert">{err}</div>}
          <div className="form-actions">
            <button className="btn" onClick={() => setEdit(null)}>Cancelar</button>
            <button className="btn primary" disabled={!edit.task.trim() || !edit.model.trim() || save.isPending} onClick={submit}>Salvar rota</button>
          </div>
        </div>
      )}
    </div>
  )
}
